import { useState, useEffect } from "react";
import {
  Button,
  Table,
  Modal,
  Tooltip,
  message,
  Layout,
  Result
} from "antd";
import type { TableProps } from "antd";
import styled from "styled-components";

import Attatchment from "../post/Attatchment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { currentPostAtom } from "../../atom";
import { getLoginUser } from "../../api/user";
import { getPostData, getPostListWithTotal } from "../../api/postAPI";

interface Props {
  rootCategoryId: number;
}

const ConferenceTable = ({ rootCategoryId }: Props) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [userName, setUserName] = useState("");
  const [selectedPostId, setSelectedPostId] = useState<number | null>(null);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [meeting, setMeeting] = useState<any>([]);
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const [searchParams, setSearchParams] = useSearchParams();

  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const currentPage = Number(searchParams.get("page")) || 1;

  const handleOpenAttachmentModal = (postId: number) => {
    setSelectedPostId(postId);
    setAttachmentModalVisible(true);
  };

  const onRowClick = async (record: any) => {
    setCurrentPost(await getPostData(record?.id));
    const postData = await getPostData(record?.id);
    const params = new URLSearchParams();
    params.set('soa', 'true');
    params.set('from', 'conference');
    params.set('kppNumber', record?.meetKppNumber || 'Unknown');
    params.set('title', postData?.title || 'Untitled');
    if (currentPage) {
      params.set('page', currentPage.toString());
    }
    navigate(`/post/${record?.id}?${params.toString()}`, { state: { soaData: record }} );
  };

  const handleCloseAttachmentModal = () => {
    setAttachmentModalVisible(false);
    setSelectedPostId(null);
  };

  const handleTableChange: TableProps<any>['onChange'] = (pagination) => {
    const newPage = pagination.current || 1;
    const newPageSize = pagination.pageSize || 20;
    setPageSize(newPageSize);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", newPage.toString());
      return newParams;
    });
  };

  const refetchData = async (page: number, size: number) => {
    setLoading(true);
    try {
      const response = await getPostListWithTotal(page, size, rootCategoryId, "id", "DESC");
      let dataList = [];
      let total = 0;
      if (Array.isArray(response)) {
         dataList = response;
      } else {
         dataList = response.list || response.data || []; 
         total = response.total || response.totalCount || response.count || 0;
      }

      const flattenedData = dataList.map((post: any) => {
        if (post.postConference) {
          return {
            ...post, 
            ...post.postConference, 
          };
        }
        return post;
      });
      
      setMeeting(flattenedData);
      setTotalCount(total);

    } catch (error) {
      console.error("Failed to fetch posts:", error);
      setIsUnauthorized(true);
    } finally {
      setLoading(false);
    }
  };

  const soaColumn: TableProps<any>["columns"] = [
    {
      title: "kpp number",
      dataIndex: "meetKppNumber",
      key: "meetKppNumber",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data ? data : "-"}</span>
          </>
        );
      },
    },
    {
      title: "gender",
      dataIndex: "meetGender",
      key: "meetGender",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data ? data : "-"}</span>
          </>
        );
      },
    },
    {
      title: "age",
      dataIndex: "meetAge",
      key: "meetAge",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data ? data : "-"}</span>
          </>
        );
      },
    },
    {
      title: "organ",
      dataIndex: "meetOrgan",
      key: "meetOrgan",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data ? data : "-"}</span>
          </>
        );
      },
    },
    {
      title: "diagnosis",
      dataIndex: "meetDiagnosis",
      key: "meetDiagnosis",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <Tooltip title={data}>
              <OverflowText
                style={{
                  display: "inline-block",
                  width: "110px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {data ? data : "-"}
              </OverflowText>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: "organization",
      dataIndex: "meetOrganization",
      key: "meetOrganization",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data ? data : "-"}</span>
          </>
        );
      },
    },
    {
      title: "hasSlide",
      dataIndex: "hasSlide",
      key: "hasSlide",
      align: "center",
      width: 110,
      render: (data: any, index: number) => {
        return (
          <>
            <span>{data}</span>
          </>
        );
      },
    },
    {
      title: "attachment",
      dataIndex: "meetAttachment",
      key: "meetAttachment",
      align: "center",
      width: 110,
      render: (data: any, record: any) => {
        return (
          <CustomA
            onClick={(event) => {
              event.stopPropagation();
              handleOpenAttachmentModal(record.id)
            }}
          >
            Show
          </CustomA>
        );
      },
    },
    {
      title: "Action",
      key: "operation",
      width: 100,
      align: "center",
      fixed: "right",
      onCell: (record) => ({
        style: { cursor: "pointer" },
        onClick: async (event) => {
          event.stopPropagation();
          if (!isAdmin && userName !== record.writer) {
            message.error({
              content: "편집 권한이 없습니다."
            });
            return;
          }

          const postData = await getPostData(record.id);
          setCurrentPost(postData);

          const params = new URLSearchParams();
          params.set('from', 'conference');
          params.set('isEdit', 'true');
          params.set('postTitle', postData?.title || 'Untitled');
          params.set('postId', String(record.id));
          params.set('kppNumber', record.meetKppNumber || 'Unknown');
          if (currentPage) {
            params.set('page', currentPage.toString());
          }

          navigate(`/write?${params.toString()}`, {
            state: {
              conferenceMode: true,
              conferenceEditMode: true,
              rootCategoryId,
              postData,
              conferenceInitialValues: {
                meetKppNumber: record.meetKppNumber,
                meetGender: record.meetGender,
                meetAge: record.meetAge,
                meetOrgan: record.meetOrgan,
                meetDiagnosis: record.meetDiagnosis,
                meetOrganization: record.meetOrganization,
              },
            },
          });
        },
      }),
      render: () => <CustomA>Edit</CustomA>,
    },
  ];

  useEffect(() => {
    refetchData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getLoginUser().then(u => {
      setIsAdmin(u?.role === 'ADMIN_MAIN');
      setUserName(u?.name || "");
    });
  }, []);

  if (isUnauthorized) {
    return (
      <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Result
          status="403"
          title="접근 권한 없음"
          subTitle="해당 게시판에 접근 권한이 없습니다."
          extra={
            <Button type="primary" onClick={() => navigate("/")}>
              메인으로 돌아가기
            </Button>
          }
        />
      </Layout.Content>
    );
  }

  return (
    <div className="soa-table-wrapper">
      <div
        className="btn-wrapper"
        style={{
        display: "flex",
        justifyContent: "flex-start",
        marginBottom: "0.5rem",
        width: "100%",
        }}
      >
        {isAdmin && <Button
          onClick={() => {
            const params = new URLSearchParams();
            params.set('from', 'conference');
            if (currentPage) {
              params.set('page', currentPage.toString());
            }
            navigate(`/write?${params.toString()}`, { state: { conferenceMode: true, rootCategoryId } });
          }}
          >
          Add
        </Button>}
      </div>
      <SoaTable
        rowKey="id"
        id="soa-table"
        loading={loading}
        columns={soaColumn}
        dataSource={meeting}
        pagination={{ 
          current: currentPage,
          total: totalCount,
          pageSize: pageSize,
          position: ["bottomCenter"],
          onChange: (page) => {
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set("page", page.toString());
              return newParams;
            });
          }
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
        onClick: () => {
          onRowClick(record);
        }
        })}
      />
      <Modal
        // title="Attachments"
        open={attachmentModalVisible}
        onCancel={handleCloseAttachmentModal}
        footer={null}
        width="40%"
        centered
      >
        {selectedPostId && <Attatchment postId={selectedPostId} />}
      </Modal>
    </div>
  )
};

const SoaTable = styled(Table)`
  #soa-table {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid #d9d9d9;

    .ant-table-thead tr > th {
      background-color: rgba(237, 237, 237, 0.94);

      &::before {
        border: 1px solid #d9d9d9;
      }
    }
    .ant-table-row {
      cursor: pointer;
      &:hover {
        // text-decoration: underline;
        color: darkcyan;
      }
    }
  }
`;

const OverflowText = styled.span`
  display: inline-block;
  width: 110px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const CustomA = styled.a(() => ({
  color: "#1677ff",
  "&:hover": {
    color: "#1699ff",
  },
}));


export default ConferenceTable;